// LiveCounters — three cards anchored by People / Power / Purpose.
//
// Every card is a clickable region picker: clicking any card advances
// to the next live region (BW → ZA → NG → ZW → BW). The numbers, flag,
// tax-authority forms, and native-language greeting all swap per
// region — these are not decorative cards, they're a second region
// picker on top of the Africa map.

import { animate, motion, useInView, useReducedMotion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { useRegionStore } from '@/stores/region';
import { useUiStore } from '@/stores/ui';
import { makeT } from '@/lib/i18n';
import { getRegion, REGION_CODES, type RegionCode } from '@nako/core';

interface RegionMeta {
  flag: string;
  market: string;
  count: number;
  taxAuth: string;
  vatForm: string;
  payeForm: string;
  lang: string;
  langCode: string;
  greeting: string;
  sub: string;
  /** CSS accent for the card border / glow when this region is active. */
  accent: string;
}

const REGION_META: Record<RegionCode, RegionMeta> = {
  BW: {
    flag: '🇧🇼',
    market: 'Botswana SMEs',
    count: 100_000,
    taxAuth: 'BURS',
    vatForm: 'VAT 002',
    payeForm: 'ITW10',
    lang: 'Setswana',
    langCode: 'tn',
    greeting: 'Dumela',
    sub: 'Sheba inbox ya gago',
    accent: '#F4A623',
  },
  ZA: {
    flag: '🇿🇦',
    market: 'South African SMEs',
    count: 2_500_000,
    taxAuth: 'SARS',
    vatForm: 'VAT201',
    payeForm: 'EMP201',
    lang: 'isiZulu',
    langCode: 'zu',
    greeting: 'Sawubona',
    sub: 'Bheka ibhokisi lakho',
    accent: '#22C55E',
  },
  NG: {
    flag: '🇳🇬',
    market: 'Nigerian SMEs',
    count: 39_000_000,
    taxAuth: 'FIRS',
    vatForm: 'FIRS VAT',
    payeForm: 'PAYE',
    lang: 'Hausa',
    langCode: 'ha',
    greeting: 'Sannu',
    sub: 'Duba akwatin saƙo',
    accent: '#0EA5E9',
  },
  ZW: {
    flag: '🇿🇼',
    market: 'Zimbabwean SMEs',
    count: 1_200_000,
    taxAuth: 'ZIMRA',
    vatForm: 'ZIMRA VAT 7',
    payeForm: 'P2',
    lang: 'Shona',
    langCode: 'sn',
    greeting: 'Mhoro',
    sub: 'Tarisa inbox yako',
    accent: '#EAB308',
  },
};

function nextRegion(code: RegionCode): RegionCode {
  const i = REGION_CODES.indexOf(code);
  return REGION_CODES[(i + 1) % REGION_CODES.length]!;
}

function formatCount(n: number): string {
  if (n >= 1_000_000) {
    const m = n / 1_000_000;
    return m >= 10 ? `${Math.round(m)}M` : `${m.toFixed(1).replace(/\.0$/, '')}M`;
  }
  if (n >= 1_000) {
    const k = n / 1_000;
    return k >= 10 ? `${Math.round(k)}k` : `${k.toFixed(1).replace(/\.0$/, '')}k`;
  }
  return String(Math.round(n));
}

export function LiveCounters() {
  const reduced = useReducedMotion();
  const rowRef = useRef<HTMLDivElement | null>(null);
  const inView = useInView(rowRef, { once: true, margin: '-100px' });
  const regionCode = useRegionStore((s) => s.code);
  const setRegion = useRegionStore((s) => s.setRegion);
  const lang = useUiStore((s) => s.lang);
  const t = makeT(lang);

  const r = REGION_META[regionCode] ?? REGION_META.BW;
  const cfg = getRegion(regionCode);
  const next = nextRegion(regionCode);
  const nextMeta = REGION_META[next];
  const advance = () => setRegion(next);

  // PAYE top marginal rate — actual per region from regionConfig.
  const payeTopRate = cfg.paye.length > 0 ? cfg.paye[cfg.paye.length - 1]!.rate : 0;

  const cards = [
    {
      key: 'people',
      eyebrow: t('live.people'),
      headlineNode: (
        <CountUp value={r.count} inView={inView} reduced={reduced ?? false} keyId={regionCode} />
      ),
      label: `${r.market} ${t('live.smes_market_suffix')}`,
      sub: `${r.taxAuth}${t('live.tax_registered_suffix')}`,
      pillRows: [
        { k: 'FLAG', v: r.flag },
        { k: 'CAP.', v: cfg.languages[0]?.name === 'English' ? cfg.displayName : cfg.displayName },
      ],
    },
    {
      key: 'pride',
      eyebrow: t('live.pride'),
      headlineNode: (
        <span className="font-display text-ink text-[clamp(1.6rem,3.5vw,2.6rem)] font-semibold leading-tight">
          {(cfg.vat.rate * 100).toFixed((cfg.vat.rate * 100) % 1 === 0 ? 0 : 1)}%
          <span className="text-muted ml-2 text-[0.5em] font-normal">VAT</span>
        </span>
      ),
      label: `${r.taxAuth} ${t('live.forms_auto_suffix')}`,
      sub: `${r.vatForm} · ${r.payeForm}`,
      pillRows: [
        { k: 'CIT', v: `${(cfg.corporateTax.standard * 100).toFixed(0)}%` },
        { k: 'PAYE', v: `${(payeTopRate * 100).toFixed(0)}%` },
      ],
    },
    {
      key: 'purpose',
      eyebrow: t('live.purpose'),
      headlineNode: (
        <span className="font-display text-ink text-[clamp(2rem,4.5vw,3.2rem)] font-semibold leading-tight">
          {r.greeting}
        </span>
      ),
      label: t('live.native_interface'),
      sub: r.sub,
      pillRows: [
        { k: 'LANG', v: r.lang },
        { k: 'CCY', v: cfg.currencySymbol },
      ],
    },
  ];

  return (
    <div
      ref={rowRef}
      className="mx-auto mt-12 grid w-full max-w-5xl grid-cols-1 gap-3 px-4 md:grid-cols-3 md:gap-4 md:px-8"
    >
      {cards.map((card, idx) => (
        <RegionCard
          key={card.key}
          card={card}
          index={idx}
          inView={inView}
          reduced={reduced ?? false}
          accent={r.accent}
          flag={r.flag}
          nextFlag={nextMeta?.flag ?? '🌍'}
          nextLabel={nextMeta ? `${nextMeta.flag} ${nextMeta.market.replace(' SMEs', '')}` : ''}
          onAdvance={advance}
        />
      ))}
    </div>
  );
}

function CountUp({
  value,
  inView,
  reduced,
  keyId,
}: {
  value: number;
  inView: boolean;
  reduced: boolean;
  keyId: string;
}) {
  const [count, setCount] = useState(reduced ? value : 0);
  useEffect(() => {
    if (reduced) {
      setCount(value);
      return;
    }
    if (!inView) return;
    setCount(0);
    const controls = animate(0, value, {
      duration: 1.6,
      ease: 'easeOut',
      onUpdate(v) {
        setCount(Math.round(v));
      },
    });
    return () => controls.stop();
    // re-run when region changes (keyId) so the number animates fresh.
  }, [value, inView, reduced, keyId]);
  return (
    <span
      aria-live="polite"
      className="font-display text-ink font-semibold leading-tight tracking-tight"
      style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)' }}
    >
      {`${formatCount(count)}+`}
    </span>
  );
}

interface RegionCardData {
  key: string;
  eyebrow: string;
  headlineNode: React.ReactNode;
  label: string;
  sub: string;
  pillRows: Array<{ k: string; v: string }>;
}

function RegionCard({
  card,
  index,
  inView,
  reduced,
  accent,
  flag,
  nextFlag,
  nextLabel,
  onAdvance,
}: {
  card: RegionCardData;
  index: number;
  inView: boolean;
  reduced: boolean;
  accent: string;
  flag: string;
  nextFlag: string;
  nextLabel: string;
  onAdvance: () => void;
}) {
  const eyebrowDelay = index * 0.08;

  const eyebrowVariants = reduced
    ? { initial: { opacity: 1, y: 0 }, animate: { opacity: 1, y: 0 } }
    : { initial: { opacity: 0, y: 4 }, animate: { opacity: 1, y: 0 } };

  return (
    <button
      type="button"
      onClick={onAdvance}
      aria-label={`Switch to ${nextLabel}`}
      className="bg-paper border-line rounded-card-lg group relative w-full overflow-hidden border p-6 text-left transition-transform hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-12px_rgba(0,0,0,0.18)] focus:outline-none focus:ring-2 focus:ring-offset-2"
      style={{
        borderRadius: 8,
        boxShadow: `inset 0 0 0 1px ${accent}22, 0 0 0 0 ${accent}00`,
      }}
    >
      {/* Accent corner — moves with region */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -right-6 -top-6 h-20 w-20 rounded-full opacity-40 blur-2xl transition-opacity group-hover:opacity-60"
        style={{ background: accent }}
      />

      {/* Flag chip (top-right) */}
      <div className="mb-3 flex items-center justify-between">
        <motion.p
          initial="initial"
          animate={inView ? 'animate' : 'initial'}
          variants={eyebrowVariants}
          transition={{ duration: 0.4, delay: eyebrowDelay, ease: [0.22, 1, 0.36, 1] }}
          className="font-mono text-xs"
          style={{ letterSpacing: '0.15em', fontSize: '0.75rem', color: accent }}
        >
          {card.eyebrow}
        </motion.p>
        <span
          aria-hidden="true"
          className="border-line inline-flex items-center gap-1 rounded-full border bg-white/70 px-2 py-0.5 text-sm backdrop-blur-sm"
        >
          {flag}
        </span>
      </div>

      {/* Region-aware content. Swap is instant — the motion stays on
          the eyebrow so the cards never appear blank during a switch. */}
      <div>
        {card.headlineNode}
        <p className="text-ink mt-2 text-sm">{card.label}</p>
        <p className="text-muted mt-1 font-mono text-xs">{card.sub}</p>

        <div className="border-line mt-4 flex items-center gap-3 border-t pt-3 font-mono text-[10px]">
          {card.pillRows.map((p) => (
            <span key={p.k} className="text-muted inline-flex items-center gap-1">
              <span className="opacity-60">{p.k}</span>
              <span className="text-ink">{p.v}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Tap hint — fades in on hover, shows where the next click goes */}
      <span
        aria-hidden="true"
        className="text-muted absolute bottom-2 right-3 inline-flex items-center gap-1 font-mono text-[10px] opacity-0 transition-opacity group-hover:opacity-100"
        style={{ letterSpacing: '0.18em' }}
      >
        TAP → {nextFlag}
        <ArrowRight size={11} />
      </span>
    </button>
  );
}
